import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight } from "lucide-react";
import React from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { sendContactEmail } from "@lib/sendContactEmail";

export interface ContactFormCopy {
  heading: string;
  fields: {
    name: {
      label: string;
      placeholder: string;
      requiredError: string;
    };
    email: {
      label: string;
      placeholder: string;
      requiredError: string;
      invalidError: string;
    };
    topic: {
      label: string;
      placeholder: string;
      requiredError: string;
      maxLengthError: string;
    };
  };
  submit: string;
  submitting: string;
  successMessage: string;
  errorMessage: string;
  responseTime: string;
  emailSubjectPrefix: string;
}

const getContactFormSchema = (copy: ContactFormCopy) =>
  z.object({
    name: z.string().trim().min(1, copy.fields.name.requiredError),
    email: z
      .string()
      .trim()
      .min(1, copy.fields.email.requiredError)
      .email(copy.fields.email.invalidError),
    topic: z
      .string()
      .trim()
      .min(1, copy.fields.topic.requiredError)
      .max(2000, copy.fields.topic.maxLengthError),
  });

type ContactFormValues = z.infer<ReturnType<typeof getContactFormSchema>>;

const inputClassName =
  "w-full px-4 py-3 rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent";

export function ContactForm({ copy }: { copy: ContactFormCopy }) {
  const contactFormSchema = React.useMemo(
    () => getContactFormSchema(copy),
    [copy],
  );

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: {
      name: "",
      email: "",
      topic: "",
    },
  });

  const onSubmit = async (data: ContactFormValues) => {
    try {
      await sendContactEmail({
        email: data.email,
        phone: "-",
        topic: `${copy.emailSubjectPrefix}: ${data.name}\n\n${data.topic}`,
      });
      toast.success(copy.successMessage);
      reset();
    } catch (error) {
      console.error(error);
      toast.error(copy.errorMessage);
    }
  };

  return (
    <div className="bg-background-light text-gray-900 rounded-2xl p-8 shadow-2xl">
      <h3 className="text-2xl font-semibold mb-6 text-center">{copy.heading}</h3>
      <form
        className="space-y-4 flex flex-col"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <div>
          <label
            htmlFor="contact-name"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {copy.fields.name.label}
          </label>
          <input
            id="contact-name"
            type="text"
            className={inputClassName}
            placeholder={copy.fields.name.placeholder}
            aria-invalid={!!errors.name}
            {...register("name")}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="contact-email"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {copy.fields.email.label}
          </label>
          <input
            id="contact-email"
            type="email"
            className={inputClassName}
            placeholder={copy.fields.email.placeholder}
            aria-invalid={!!errors.email}
            {...register("email")}
          />
          {errors.email && (
            <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
          )}
        </div>

        <div>
          <label
            htmlFor="contact-topic"
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {copy.fields.topic.label}
          </label>
          <textarea
            id="contact-topic"
            rows={3}
            maxLength={2000}
            className={inputClassName}
            placeholder={copy.fields.topic.placeholder}
            aria-invalid={!!errors.topic}
            {...register("topic")}
          />
          {errors.topic && (
            <p className="mt-1 text-sm text-red-600">{errors.topic.message}</p>
          )}
        </div>

        <button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-background hover:bg-surface-dark disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium py-4 rounded-xl transition-colors flex items-center justify-center gap-2 mt-2"
        >
          {isSubmitting ? copy.submitting : copy.submit}{" "}
          <ArrowRight size={18} />
        </button>

        <p className="text-xs text-center text-gray-500 mt-2">
          {copy.responseTime}
        </p>
      </form>
    </div>
  );
}
